import * as React from 'react';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import clsx from 'clsx';

import { get } from 'lodash';
import { Card } from '@maiia/design-system';
import { useSelector } from 'react-redux';
import { useCentersTree } from '@/src/hooks';

import AvailabilityMobile from '@/components/organisms/ProfileContent/AvailabilityMobile';
import useClosestAvailabilities from '@/src/hooks/useClosestAvailabilities';
import Expertises from '@/components/molecules/Hcd/Expertises';

import AccessContent from '@/components/molecules/Hcd/AccessContent';
import {
  hasAnyVisibleAddress,
  hasInformation,
  hasVisibleAddress,
} from '@/components/utils/card';
import Schedules, {
  hasPractitioner,
  hasSchedules,
} from '@/components/molecules/Hcd/Schedules';
import { getPhoneNumber } from '@/components/utils';
import Information from '@/components/molecules/Hcd/Information';
import AppointmentForm from '@/components/organisms/Forms/AppointmentForm';
import NoClientNoAvailability from '@/components/organisms/ProfileContent/NoClientNoAvailability';
import PractitionerNoClient from '@/components/organisms/PractitionerNoClient/PractitionerNoClient';
import PractitionerList from '@/components/molecules/Hcd/PractitionerList';
import ChildCenters from '@/components/molecules/Hcd/ChildCenters';
import CentersTree from '@/components/molecules/CentersTree/CentersTree';

type Props = {
  card: CardHcd;
  cards: Array<CardHcd>;
};

/*
    Some duplicated code with PharmacyContent and PratContent, but
    it's easier to split them instead of making one big component with all the
    checks and blocks. Especially as we enrich them and add new specific cases.
 */
const CenterContent = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const { card, cards } = props;
  const showAddressInformations = hasVisibleAddress(card);
  const isClientDocavenue = card?.settings?.clientDocavenue;
  const childCenters = (card.center?.childCenters ?? []).filter(
    c => c.isVisibleOnline,
  );
  const hasDetails =
    hasInformation(card) ||
    (showAddressInformations && (hasSchedules(card) || getPhoneNumber(card)));
  const expertises = get(card, 'publicInformation.expertises', []);

  useCentersTree({ card, cards });

  const centersTreeItems = useSelector(state =>
    get(state, 'centersTree.items'),
  );

  const {
    closestAvailabilities,
    isLoading: isLoadingAvailabilities,
  } = useClosestAvailabilities(card);

  const appointmentFormComponent =
    closestAvailabilities?.availabilityCount > 0 &&
    (card?.consultationReasons?.length ?? 0) > 0 ? (
      <div id="rdv" className="profile-content__rdv">
        <AppointmentForm card={card} />
      </div>
    ) : (
      <NoClientNoAvailability
        card={card}
        isLoadingAvailabilities={isLoadingAvailabilities}
      />
    );

  return (
    <div className={clsx('profile-content')} ref={ref}>
      <div className="profile-content__info">
        {centersTreeItems.length > 0 && (
          <CentersTree centers={centersTreeItems} card={card} />
        )}

        {closestAvailabilities?.availabilityCount > 0 && (
          <AvailabilityMobile
            closestAvailabilities={closestAvailabilities}
            timezone={card?.settings?.timeZone}
          />
        )}

        {isClientDocavenue && expertises.length > 0 && (
          <Card hasOldStyle>
            <div className="flex flex-col gap-4">
              <Expertises items={expertises} />
            </div>
          </Card>
        )}

        {showAddressInformations && (
          <Card hasOldStyle id="access">
            <AccessContent card={card} />
          </Card>
        )}

        {hasDetails && (
          <Card hasOldStyle id="infos-sup">
            <Information card={card} />
            {showAddressInformations && <Schedules card={card} />}
          </Card>
        )}

        {hasPractitioner(card) && (
          <Card hasOldStyle>
            <PractitionerList card={card} />
          </Card>
        )}

        {/* BLOC CENTER LIST */}
        {childCenters?.length > 0 && (
          <div
            className={clsx(
              'card center',
              !showAddressInformations &&
                hasAnyVisibleAddress(card) &&
                'has-map',
            )}
          >
            <ChildCenters card={card} items={childCenters} />
          </div>
        )}
      </div>

      {isClientDocavenue ? (
        appointmentFormComponent
      ) : (
        <PractitionerNoClient card={card} />
      )}
    </div>
  );
});

CenterContent.displayName = 'CenterContent';

export default React.memo(CenterContent);
